// ActuPage — Page Actu style Facebook
// Utilise le même header et menu que le reste du site
// Garde la loupe pour recherche réseau + hamburger menu
// Design pixel-perfect pour Samsung Galaxy A54 (390×844px)

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Search, X, ChevronLeft, ChevronRight, Filter, Image as ImageIcon, MapPin, Smile, Video, Link2, Camera } from 'lucide-react';
import UnifiedHeader from '../components/layout/UnifiedHeader';
import MainNavigation from '../components/layout/MainNavigation';
import UniversalBanner from '../components/layout/UniversalBanner';
import UnifiedFilterSystem, { FilterButton, FilterContextPhrase } from '../components/filters/UnifiedFilterSystem';
import { useDemoMode } from '../components/demo/DemoModeProvider';

import { API_URL } from '../config/api';

// ═══ CATÉGORIES RECHERCHE RÉSEAU — Même style que MainNavigation ═══
const SEARCH_CATEGORIES = [
  { id: 'actu', label: 'Actu', path: '/reseau' },
  { id: 'artisans', label: 'Artisans', path: '/artisans' },
  { id: 'marketplace', label: 'Marketplace', path: '/produits' },
  { id: 'annonces', label: 'Particuliers', path: '/annonces' },
  { id: 'emploi', label: 'Emploi', path: '/emploi' },
  { id: 'reseau-pros', label: 'Réseau Pro', path: '/reseau-pros' },
];

// ═══ COULEURS AVATARS ═══
const AVATAR_COLORS = ['#2D5A0E', '#0D4F8C', '#A83D1C', '#534AB7', '#E8650A', '#1a2a4a', '#7C3AED', '#059669'];

// ═══ DONNÉES STORIES - Vide en production, rempli par l'API ═══
const DEMO_STORIES = []; // Production: pas de données démo

// ═══ COMPOSANT CERCLE STORY ═══
const StoryCircle = ({ story, isAdd, onClick, isViewed }) => {
  return (
    <div onClick={onClick} style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '4px',
      flexShrink: 0,
      cursor: 'pointer',
    }}>
      <div style={{
        width: '56px',
        height: '56px',
        borderRadius: '50%',
        border: isAdd ? '2px dashed #E8650A' : `2.5px solid ${isViewed ? '#D1D5DB' : '#E8650A'}`,
        padding: '2px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        {isAdd ? (
          <div style={{
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            background: '#FFF3EC',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Plus size={24} color="#E8650A" />
          </div>
        ) : (
          <img 
            src={story?.author_photo} 
            alt={story?.author_name}
            style={{
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              objectFit: 'cover',
            }}
          />
        )}
      </div>
      <span style={{
        fontSize: '10px',
        color: '#374151',
        maxWidth: '56px',
        textAlign: 'center',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>
        {isAdd ? 'Publier' : story?.author_name?.split(' ')[0]}
      </span>
    </div>
  );
};

// ═══ VIEWER STORY PLEIN ÉCRAN ═══
const StoryViewer = ({ stories, initialIndex, onClose, onMarkViewed }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const progressRef = useRef(null);
  const touchStartX = useRef(0);
  
  const currentStory = stories[currentIndex];
  
  // Marquer comme vue
  useEffect(() => {
    if (currentStory && !currentStory.viewed) {
      onMarkViewed(currentStory.id);
    }
  }, [currentIndex, currentStory, onMarkViewed]);
  
  // Timer progression (5 secondes)
  useEffect(() => {
    if (isPaused) return;
    
    const duration = 5000;
    const interval = 50;
    let elapsed = 0;
    
    progressRef.current = setInterval(() => {
      elapsed += interval;
      setProgress((elapsed / duration) * 100);
      
      if (elapsed >= duration) {
        // Passer à la story suivante
        if (currentIndex < stories.length - 1) {
          setCurrentIndex(prev => prev + 1);
          setProgress(0);
        } else {
          onClose();
        }
      }
    }, interval);
    
    return () => clearInterval(progressRef.current);
  }, [currentIndex, isPaused, stories.length, onClose]);
  
  const goNext = useCallback(() => {
    if (currentIndex < stories.length - 1) {
      setCurrentIndex(prev => prev + 1);
      setProgress(0);
    } else {
      onClose();
    }
  }, [currentIndex, stories.length, onClose]);
  
  const goPrev = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
      setProgress(0);
    }
  }, [currentIndex]);
  
  // Gestion du swipe
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    setIsPaused(true);
  };
  
  const handleTouchEnd = (e) => {
    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX;
    
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        goNext();
      } else {
        goPrev();
      }
    }
    setIsPaused(false);
  };
  
  // Tap gauche/droite
  const handleTap = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const width = rect.width;
    
    if (x < width / 3) {
      goPrev();
    } else if (x > (width * 2) / 3) {
      goNext();
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: 'fixed',
        inset: 0,
        background: '#000000',
        zIndex: 300,
      }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onClick={handleTap}
    >
      {/* Barres de progression */}
      <div style={{
        position: 'absolute',
        top: '12px',
        left: '12px',
        right: '12px',
        display: 'flex',
        gap: '4px',
        zIndex: 10,
      }}>
        {stories.map((_, idx) => (
          <div key={idx} style={{
            flex: 1,
            height: '2px',
            background: 'rgba(255,255,255,0.3)',
            borderRadius: '1px',
            overflow: 'hidden',
          }}>
            <div style={{
              height: '100%',
              background: '#FFFFFF',
              borderRadius: '1px',
              width: idx < currentIndex ? '100%' : idx === currentIndex ? `${progress}%` : '0%',
              transition: idx === currentIndex ? 'none' : 'width 0.1s',
            }} />
          </div>
        ))}
      </div>
      
      {/* Infos auteur */}
      <div style={{
        position: 'absolute',
        top: '28px',
        left: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        zIndex: 10,
      }}>
        <img 
          src={currentStory?.author_photo}
          alt={currentStory?.author_name}
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            border: '2px solid #fff',
            objectFit: 'cover',
          }}
        />
        <div>
          <div style={{
            color: '#FFFFFF',
            fontSize: '14px',
            fontWeight: 600,
          }}>
            {currentStory?.author_name}
          </div>
          <div style={{
            color: 'rgba(255,255,255,0.7)',
            fontSize: '12px',
          }}>
            {currentStory?.author_job} · {currentStory?.author_city} · {currentStory?.time}
          </div>
        </div>
      </div>
      
      {/* Bouton fermer */}
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        style={{
          position: 'absolute',
          top: '28px',
          right: '12px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          zIndex: 10,
          padding: '8px',
        }}
      >
        <X size={28} color="#FFFFFF" />
      </button>
      
      {/* Image story */}
      <img
        src={currentStory?.image}
        alt="Story"
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
        }}
      />
      
      {/* Texte story */}
      <div style={{
        position: 'absolute',
        bottom: '80px',
        left: '16px',
        right: '16px',
        color: '#FFFFFF',
        fontSize: '15px',
        fontWeight: 600,
        textShadow: '0 1px 4px rgba(0,0,0,0.5)',
        zIndex: 10,
      }}>
        {currentStory?.text}
      </div>
      
      {/* Indicateurs navigation */}
      {currentIndex > 0 && (
        <div style={{
          position: 'absolute',
          left: '8px',
          top: '50%',
          transform: 'translateY(-50%)',
          color: 'rgba(255,255,255,0.5)',
          zIndex: 10,
        }}>
          <ChevronLeft size={32} />
        </div>
      )}
      {currentIndex < stories.length - 1 && (
        <div style={{
          position: 'absolute',
          right: '8px',
          top: '50%',
          transform: 'translateY(-50%)',
          color: 'rgba(255,255,255,0.5)',
          zIndex: 10,
        }}>
          <ChevronRight size={32} />
        </div>
      )}
    </motion.div>
  );
};

// ═══ COMPOSANT STORIES BAR ═══
const StoriesBar = () => {
  const navigate = useNavigate();
  const [stories, setStories] = useState(DEMO_STORIES);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);
  
  // Trier: non vues en premier
  const sortedStories = [...stories].sort((a, b) => {
    if (a.viewed === b.viewed) return 0;
    return a.viewed ? 1 : -1;
  });
  
  const handleStoryClick = (index) => {
    setViewerIndex(index);
    setViewerOpen(true);
  };
  
  const handleMarkViewed = useCallback((storyId) => {
    setStories(prev => prev.map(s => 
      s.id === storyId ? { ...s, viewed: true } : s
    ));
  }, []);
  
  const handleAddClick = () => {
    // TODO: Vérifier si connecté, sinon rediriger
    navigate('/reseau/connexion');
  };
  
  return (
    <>
      <div style={{
        display: 'flex',
        gap: '12px',
        overflowX: 'auto',
        padding: '12px 16px',
        scrollbarWidth: 'none',
        background: '#FFFFFF',
        borderBottom: '1px solid #F3F4F6',
        msOverflowStyle: 'none',
      }}>
        {/* Bouton Publier */}
        <StoryCircle isAdd onClick={handleAddClick} />
        
        {/* Stories */}
        {sortedStories.map((story, index) => (
          <StoryCircle
            key={story.id}
            story={story}
            isViewed={story.viewed}
            onClick={() => handleStoryClick(index)}
          />
        ))}
      </div>
      
      {/* Viewer plein écran */}
      <AnimatePresence>
        {viewerOpen && (
          <StoryViewer
            stories={sortedStories}
            initialIndex={viewerIndex}
            onClose={() => setViewerOpen(false)}
            onMarkViewed={handleMarkViewed}
          />
        )}
      </AnimatePresence>
    </>
  );
};

// ═══ COMPOSANT POST ═══
const PostCard = ({ post, onNavigateToPro }) => {
  const [liked, setLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(post.likes_count || post.likes || 0);
  const commentsCount = post.comments_count || post.comments || 0;
  const { requireAuth } = useDemoMode();

  const getInitials = (name) => {
    if (!name) return 'AB';
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  const getTimeAgo = (date) => {
    if (!date) return '1h';
    const now = new Date();
    const postDate = new Date(date);
    const diffMs = now - postDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 60) return `${diffMins}min`;
    if (diffHours < 24) return `${diffHours}h`;
    return `${diffDays}j`;
  };

  const handleLike = () => {
    if (!requireAuth('aimer cette publication')) return;
    setLiked(!liked);
    setLikesCount(prev => liked ? prev - 1 : prev + 1);
  };
  
  const handleComment = () => {
    requireAuth('commenter cette publication');
  };
  
  const handleShare = () => {
    requireAuth('partager cette publication');
  };

  const avatarColor = post.avatarColor || AVATAR_COLORS[Math.floor(Math.random() * AVATAR_COLORS.length)];
  const postType = post.post_type || post.type || post.category || 'Réalisation';
  const authorName = post.author?.name || post.author?.display_name || post.author?.username || post.author_name || 'Utilisateur';
  const city = post.location || post.author?.city || post.author_city || post.city || 'France';
  const profilePhoto = post.author?.avatar || post.author?.profile_photo;

  // Format content with hashtags
  const formatContent = (text) => {
    if (!text) return '';
    return text.split(/(\#\w+)/g).map((part, i) => {
      if (part.startsWith('#')) {
        return <span key={i} style={{ color: '#E8650A', fontWeight: 500 }}>{part}</span>;
      }
      return part;
    });
  };

  return (
    <div style={{
      background: '#fff',
      marginBottom: '8px',
      borderTop: '1px solid #EBEBEB',
      borderBottom: '1px solid #EBEBEB',
    }}>
      {/* HEADER */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '12px 16px 8px',
      }}>
        {profilePhoto ? (
          <img 
            src={profilePhoto} 
            alt={authorName}
            style={{
              width: '42px',
              height: '42px',
              borderRadius: '50%',
              objectFit: 'cover',
            }}
          />
        ) : (
          <div style={{
            width: '42px',
            height: '42px',
            borderRadius: '50%',
            background: avatarColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            color: '#fff',
            fontSize: '15px',
            flexShrink: 0,
          }}>
            {getInitials(authorName)}
          </div>
        )}
        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: '14px',
            fontWeight: 700,
            color: '#1a2a4a',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
          }}>
            {authorName}
            <span style={{
              width: '16px',
              height: '16px',
              background: '#2D5A0E',
              borderRadius: '50%',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontSize: '9px',
            }}>✓</span>
          </div>
          <div style={{
            fontSize: '11px',
            color: '#888',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            marginTop: '2px',
          }}>
            <span style={{
              background: '#FFF3EC',
              color: '#E8650A',
              borderRadius: '20px',
              padding: '1px 8px',
              fontSize: '10px',
              fontWeight: 600,
            }}>
              {postType}
            </span>
            <span>📍 {city}</span>
            <span>· {getTimeAgo(post.created_at)}</span>
          </div>
        </div>
        <div style={{ fontSize: '18px', color: '#bbb', cursor: 'pointer' }}>···</div>
      </div>

      {/* TEXT */}
      {post.content && (
        <div style={{
          padding: '0 16px 10px',
          fontSize: '14px',
          color: '#333',
          lineHeight: 1.5,
        }}>
          {formatContent(post.content)}
        </div>
      )}

      {/* MEDIA */}
      {post.media && post.media.length > 0 ? (
        post.media.length === 1 ? (
          <div style={{
            width: '100%',
            aspectRatio: '4/3',
            background: '#e5e5e5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            {post.media[0].url ? (
              <img 
                src={post.media[0].url} 
                alt="" 
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                loading="lazy"
              />
            ) : (
              <span style={{ fontSize: '48px' }}>{post.icon || '🏗️'}</span>
            )}
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '2px',
          }}>
            {post.media.slice(0, 4).map((m, i) => (
              <div key={i} style={{
                aspectRatio: '1',
                background: '#e5e5e5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                {m.url ? (
                  <img src={m.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
                ) : (
                  <span style={{ fontSize: '32px' }}>🏗️</span>
                )}
              </div>
            ))}
          </div>
        )
      ) : post.image_url ? (
        <div style={{
          width: '100%',
          aspectRatio: '4/3',
          background: '#e5e5e5',
        }}>
          <img 
            src={post.image_url} 
            alt="" 
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            loading="lazy"
          />
        </div>
      ) : (
        <div style={{
          width: '100%',
          aspectRatio: '4/3',
          background: post.bgColor || '#E6F1FB',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '48px',
        }}>
          {post.icon || '🏗️'}
        </div>
      )}

      {/* STATS */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 16px',
        borderTop: '1px solid #f5f5f5',
      }}>
        <div style={{
          fontSize: '12px',
          color: '#888',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
        }}>
          <div style={{ display: 'flex' }}>
            <div style={{
              width: '18px',
              height: '18px',
              borderRadius: '50%',
              background: '#E8650A',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '10px',
              border: '1.5px solid #fff',
            }}>❤️</div>
            {likesCount > 10 && (
              <div style={{
                width: '18px',
                height: '18px',
                borderRadius: '50%',
                background: '#1a2a4a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                marginLeft: '-4px',
                border: '1.5px solid #fff',
              }}>👍</div>
            )}
          </div>
          {likesCount} j'aime
        </div>
        <div style={{ fontSize: '12px', color: '#888' }}>
          {commentsCount} commentaires
        </div>
      </div>

      {/* ACTIONS */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        borderTop: '1px solid #EBEBEB',
      }}>
        <div 
          onClick={handleLike}
          data-testid="post-like-btn"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            padding: '10px',
            cursor: 'pointer',
            fontSize: '13px',
            color: liked ? '#E8650A' : '#666',
            fontWeight: 500,
          }}
        >
          <span style={{ fontSize: '16px' }}>{liked ? '❤️' : '🤍'}</span> J'aime
        </div>
        <div 
          onClick={handleComment}
          data-testid="post-comment-btn"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            padding: '10px',
            cursor: 'pointer',
            fontSize: '13px',
            color: '#666',
            fontWeight: 500,
          }}
        >
          <span style={{ fontSize: '16px' }}>💬</span> Commenter
        </div>
        <div 
          onClick={handleShare}
          data-testid="post-share-btn"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            padding: '10px',
            cursor: 'pointer',
            fontSize: '13px',
            color: '#666',
            fontWeight: 500,
          }}
        >
          <span style={{ fontSize: '16px' }}>↗️</span> Partager
        </div>
      </div>

      {/* BOUTIQUE */}
      <div 
        onClick={() => onNavigateToPro(post.author_id || post.user_id)}
        style={{
          margin: '0 16px 12px',
          background: '#FFF3EC',
          border: '1px solid #fcd9c0',
          borderRadius: '10px',
          padding: '10px 12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
        }}
      >
        <div style={{ fontSize: '12px', color: '#E8650A', fontWeight: 600 }}>
          🏪 Voir la boutique de {authorName}
        </div>
        <button style={{
          background: '#E8650A',
          color: '#fff',
          border: 'none',
          borderRadius: '8px',
          padding: '6px 12px',
          fontSize: '11px',
          fontWeight: 700,
          cursor: 'pointer',
        }}>
          Visiter →
        </button>
      </div>
    </div>
  );
};

// ═══ PAGE RECHERCHE RÉSEAU — Design unifié avec la plateforme ═══
const SearchPage = ({ isOpen, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('actu');
  const [results, setResults] = useState([]);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  useEffect(() => {
    const fetchResults = async () => {
      try {
        const response = await fetch(`${API_URL}/api/ecosystem/feed/discover?limit=10`);
        if (response.ok) {
          const data = await response.json();
          const postsArray = Array.isArray(data) ? data : (data.posts || data.items || []);
          setResults(postsArray);
        }
      } catch (error) {
        setResults([]);
      }
    };
    if (isOpen) fetchResults();
  }, [isOpen, activeCategory]);

  const handleCategoryClick = (cat) => {
    setActiveCategory(cat.id);
    // Si on clique sur une catégorie autre que Actu, on ferme et navigue
    if (cat.id !== 'actu') {
      onClose();
      navigate(cat.path);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      onClose();
      // Redirection vers la page correspondante avec la recherche
      const cat = SEARCH_CATEGORIES.find(c => c.id === activeCategory);
      navigate(`${cat?.path || '/reseau'}?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: '#fff',
            zIndex: 10000,
            maxWidth: '428px',
            margin: '0 auto',
          }}
        >
          {/* ═══ HEADER — Même style que UnifiedHeader mobile ═══ */}
          <div style={{
            padding: '12px 16px',
            background: '#fff',
            borderBottom: '1px solid #E2E8F0',
          }}>
            <form onSubmit={handleSearch} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {/* Barre de recherche — loupe unique à gauche */}
              <div 
                style={{
                  flex: 1,
                  height: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  background: '#F8FAFC',
                  border: '2px solid #E2E8F0',
                  borderRadius: '24px',
                  padding: '0 16px',
                }}
              >
                <Search size={18} color="#94A3B8" style={{ flexShrink: 0 }} />
                <input
                  ref={inputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Rechercher une actualité, un pro..."
                  style={{
                    flex: 1,
                    border: 'none',
                    background: 'transparent',
                    fontSize: '15px',
                    color: '#1E3A5F',
                    outline: 'none',
                  }}
                />
              </div>
              
              {/* Bouton Fermer */}
              <button
                type="button"
                onClick={onClose}
                style={{
                  fontSize: '14px',
                  color: '#E8650A',
                  fontWeight: 600,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '8px',
                }}
              >
                Fermer
              </button>
            </form>
          </div>

          {/* ═══ NAVIGATION — Même style que MainNavigation ═══ */}
          <nav style={{
            background: '#FFF8F4',
            borderBottom: '0.5px solid #FCE4D4',
            overflowX: 'auto',
            display: 'flex',
            WebkitOverflowScrolling: 'touch',
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
          }}>
            <div style={{ display: 'flex', padding: '0 12px' }}>
              {SEARCH_CATEGORIES.map((cat) => {
                const isActive = activeCategory === cat.id;
                return (
                  <button
                    key={cat.id}
                    onClick={() => handleCategoryClick(cat)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '10px 10px',
                      fontSize: '12px',
                      fontWeight: isActive ? 600 : 400,
                      color: isActive ? '#E8650A' : '#555555',
                      background: 'none',
                      border: 'none',
                      borderBottom: isActive ? '2px solid #E8650A' : '2px solid transparent',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {cat.label}
                  </button>
                );
              })}
            </div>
          </nav>

          {/* ═══ RÉSULTATS ═══ */}
          <div style={{
            padding: '16px',
            overflowY: 'auto',
            height: 'calc(100vh - 130px)',
            background: '#F5F5F7',
          }}>
            <div style={{
              fontSize: '11px',
              fontWeight: 700,
              color: '#888',
              textTransform: 'uppercase',
              letterSpacing: '0.8px',
              marginBottom: '12px',
            }}>
              Résultats populaires
            </div>
            
            {results.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '40px 20px', 
                color: '#888',
                background: '#fff',
                borderRadius: '12px',
                border: '1px solid #EBEBEB',
              }}>
                Aucun résultat pour le moment
              </div>
            ) : (
              results.slice(0, 5).map((post, index) => (
                <div key={post._id || index} style={{
                  background: '#fff',
                  border: '1px solid #EBEBEB',
                  borderRadius: '12px',
                  padding: '12px',
                  marginBottom: '10px',
                  display: 'flex',
                  gap: '12px',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}>
                  <div style={{
                    width: '52px',
                    height: '52px',
                    borderRadius: '10px',
                    background: '#EAF3DE',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px',
                    flexShrink: 0,
                  }}>
                    {post.icon || '🏗️'}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ 
                      fontSize: '13px', 
                      fontWeight: 700, 
                      color: '#1E3A5F', 
                      marginBottom: '3px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {post.content?.substring(0, 40) || 'Publication'}...
                    </div>
                    <div style={{ fontSize: '11px', color: '#888', marginBottom: '4px' }}>
                      {post.author_name || 'Artisan'} · {post.author_city || 'France'} · ✓ Vérifié
                    </div>
                    <div style={{
                      display: 'inline-flex',
                      background: '#FFF3EC',
                      color: '#E8650A',
                      borderRadius: '20px',
                      padding: '2px 8px',
                      fontSize: '10px',
                      fontWeight: 600,
                    }}>
                      {post.type || 'Réalisation'}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// ═══ LISTE DES MÉTIERS POUR LE FILTRE (identique au mobile) ═══
const METIERS_LIST = [
  { value: 'tous', label: 'Tous les métiers', color: '#1E3A5F' },
  { value: 'pisciniste', label: 'Pisciniste', color: '#06B6D4' },
  { value: 'plombier', label: 'Plombier', color: '#3B82F6' },
  { value: 'electricien', label: 'Électricien', color: '#EAB308' },
  { value: 'macon', label: 'Maçon', color: '#EA580C' },
  { value: 'carreleur', label: 'Carreleur', color: '#10B981' },
  { value: 'peintre', label: 'Peintre', color: '#EC4899' },
  { value: 'menuisier', label: 'Menuisier', color: '#D97706' },
  { value: 'couvreur', label: 'Couvreur', color: '#8B5CF6' },
  { value: 'chauffagiste', label: 'Chauffagiste', color: '#EF4444' },
  { value: 'paysagiste', label: 'Paysagiste', color: '#22C55E' },
  { value: 'climaticien', label: 'Climaticien', color: '#0891B2' },
  { value: 'jardinier', label: 'Jardinier', color: '#16A34A' },
  { value: 'facadier', label: 'Façadier', color: '#A855F7' },
  { value: 'serrurier', label: 'Serrurier', color: '#64748B' },
  { value: 'charpentier', label: 'Charpentier', color: '#92400E' },
  { value: 'plaquiste', label: 'Plaquiste', color: '#6366F1' },
  { value: 'cuisiniste', label: 'Cuisiniste', color: '#F97316' },
  { value: 'architecte', label: 'Architecte', color: '#1E3A5F' },
];

// ═══ ONGLETS DU FEED (comme mobile) ═══
const FEED_TABS = [
  { id: 'tout', label: 'Tout' },
  { id: 'a_cote', label: 'À côté' },
  { id: 'suivis', label: 'Suivis' },
  { id: 'promos', label: 'Promos' },
];

// ═══ COMPOSANT PRINCIPAL ═══
export default function ActuPage({ embedded = false }) {
  const navigate = useNavigate();
  const { requireAuth } = useDemoMode();
  const [searchOpen, setSearchOpen] = useState(false);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({});
  
  // Nouveaux filtres synchronisés avec mobile
  const [selectedTab, setSelectedTab] = useState('tout');
  const [selectedMetier, setSelectedMetier] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  
  // Popup inscription pour les stories
  const [showAuthPopup, setShowAuthPopup] = useState(false);
  
  // Modal de publication rapide
  const [showQuickPost, setShowQuickPost] = useState(false);
  const [postContent, setPostContent] = useState('');
  const [postPhoto, setPostPhoto] = useState(null); // UNE seule photo
  const [postVideo, setPostVideo] = useState(null);
  const [postLocation, setPostLocation] = useState('');
  const [postFeeling, setPostFeeling] = useState('');
  const [postProductLink, setPostProductLink] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Galerie (plusieurs photos avec titre)
  const [isGalleryMode, setIsGalleryMode] = useState(false);
  const [galleryTitle, setGalleryTitle] = useState('');
  const [galleryPhotos, setGalleryPhotos] = useState([]);

  // Vérifier si l'utilisateur est connecté
  const isLoggedIn = () => {
    const token = localStorage.getItem('auth_token') || localStorage.getItem('client_token') || localStorage.getItem('ecosystem_token');
    const userId = localStorage.getItem('user_id');
    return !!(token || userId);
  };
  
  // Ouvrir le modal de publication
  const handleOpenQuickPost = () => {
    if (isLoggedIn()) {
      setShowQuickPost(true);
    } else {
      setShowAuthPopup(true);
    }
  };
  
  // Fermer et reset le modal
  const handleCloseQuickPost = () => {
    setShowQuickPost(false);
    setPostContent('');
    setPostPhoto(null);
    setPostVideo(null);
    setPostLocation('');
    setPostFeeling('');
    setPostProductLink(null);
    setIsGalleryMode(false);
    setGalleryTitle('');
    setGalleryPhotos([]);
  };
  
  // Publier le post
  const handlePublishPost = async () => {
    if (!postContent.trim() && !postPhoto && !postVideo && galleryPhotos.length === 0) {
      toast.error('Ajoute du contenu à ta publication');
      return;
    }
    
    if (isGalleryMode && !galleryTitle.trim()) {
      toast.error('Donne un titre à ta galerie');
      return;
    }
    
    if (isGalleryMode && galleryPhotos.length < 2) {
      toast.error('Ajoute au moins 2 photos pour une galerie');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const userId = localStorage.getItem('user_id') || 'demo_user';
      
      // Préparer les photos (photo unique ou galerie)
      const photos = isGalleryMode ? galleryPhotos : (postPhoto ? [postPhoto] : []);
      
      const postData = {
        user_id: userId,
        type: isGalleryMode ? 'gallery' : 'post',
        content: postContent,
        photos: photos,
        visibility: 'public',
        gallery_title: isGalleryMode ? galleryTitle : null,
        mood: postFeeling || null,
        location: postLocation || null,
        linked_product_id: postProductLink?.id || null,
      };
      
      const response = await fetch(`${API_URL}/api/cockpit/posts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(postData)
      });
      
      if (response.ok) {
        toast.success('Publication envoyée !');
        handleCloseQuickPost();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la publication');
      }
    } catch (error) {
      console.error('Error publishing post:', error);
      toast.error('Erreur lors de la publication');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Handler pour créer une story
  const handleCreateStory = () => {
    if (isLoggedIn()) {
      navigate('/cockpit-pro/reseau-social/story/create');
    } else {
      setShowAuthPopup(true);
    }
  };

  // Handler pour le bouton Suivre
  const handleFollow = (proName) => {
    requireAuth(`suivre ${proName}`);
  };

  // Géolocalisation pour "À côté"
  useEffect(() => {
    if (selectedTab === 'a_cote' && !userLocation) {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
          },
          () => {
            // Silently fail - user can still use other tabs
          }
        );
      }
    }
  }, [selectedTab, userLocation]);

  // Fetch posts via /api/feed (unifié avec mobile)
  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.append('tab', selectedTab);
        params.append('limit', '30');
        
        if (selectedMetier) {
          params.append('metier', selectedMetier);
        }
        
        if (selectedTab === 'a_cote' && userLocation) {
          params.append('lat', userLocation.lat);
          params.append('lng', userLocation.lng);
          params.append('radius', '30');
        }
        
        const userId = localStorage.getItem('user_id') || 'demo_user';
        if (selectedTab === 'suivis') {
          params.append('user_id', userId);
        }
        
        const response = await fetch(`${API_URL}/api/feed?${params.toString()}`);
        if (response.ok) {
          const data = await response.json();
          setPosts(data.items || []);
        }
      } catch (error) {
        console.error('Error fetching posts:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, [selectedTab, selectedMetier, userLocation]);

  const handleNavigateToPro = (proId) => {
    if (proId) {
      navigate(`/artisans/${proId}`);
    }
  };

  // Navigation secondaire supprimée - déjà présente dans le sous-menu horizontal

  return (
    <div style={{
      background: '#F5F5F7',
      ...(embedded ? {} : { minHeight: '100vh' }),
    }}>
      {/* ═══ HEADER + NAV - masqués en mode cockpit (le cockpit a ses propres) ═══ */}
      {!embedded && <UnifiedHeader hideSearchOnMobile={true} />}
      {!embedded && <MainNavigation />}
      {!embedded && <div className="h-[44px] md:hidden" />}
      {!embedded && <div className="hidden md:block" style={{ height: '120px' }} />}

      {/* ═══ HERO BANNER RÉSEAU - Masqué sur Desktop ═══ */}

      {/* Badges de confiance - Masqué sur Desktop */}

      {/* ═══ BANNIÈRE CONVERSION - Mobile uniquement ═══ */}
      <div className="md:hidden">
        <UniversalBanner variant="reseau" className="py-6" />
      </div>

      {/* STORIES - Mobile et Tablette uniquement */}
      <div className="lg:hidden">
        <StoriesBar />
      </div>

      {/* ═══ LAYOUT DESKTOP 3 COLONNES — SIDEBARS FIXES ═══ */}
      {/* En mode cockpit (embedded): hauteur fixe, seul le centre défile */}
      {/* En mode standalone: les sidebars sont sticky, page entière défile */}
      <div
        className="hidden lg:flex"
        style={embedded ? {
          height: 'calc(100vh - 176px)',
          overflow: 'hidden',
          maxWidth: '1400px',
          margin: '0 auto',
          padding: '8px 24px',
          gap: '24px',
        } : {
          display: 'flex',
          minHeight: 'calc(100vh - 200px)',
          maxWidth: '1400px',
          margin: '0 auto',
          padding: '16px 24px',
          gap: '24px',
          alignItems: 'flex-start',
        }}
      >
          {/* SIDEBAR GAUCHE */}
          <div style={{
            width: '280px',
            flexShrink: 0,
            ...(embedded ? { overflowY: 'auto', height: '100%' } : {}),
          }}>
            <div style={embedded ? {} : {
              position: 'sticky',
              top: embedded ? '8px' : '100px',
              maxHeight: 'calc(100vh - 80px)',
              overflowY: 'auto',
            }}>
              <div style={{
                background: '#fff',
                borderRadius: '16px',
                padding: '16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              }}>
                <h3 style={{
                  fontSize: '14px',
                  fontWeight: 700,
                  color: '#1a2a4a',
                  marginBottom: '16px',
                  paddingLeft: '8px',
                }}>
                  Explorer le Réseau
                </h3>
                
                {/* Onglets principaux: Tout, À côté, Suivis, Promos */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '6px',
                  marginBottom: '16px',
                }}>
                  {FEED_TABS.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setSelectedTab(tab.id)}
                      style={{
                        padding: '10px 12px',
                        background: selectedTab === tab.id ? '#FFF3EC' : '#F5F5F7',
                        border: selectedTab === tab.id ? '1px solid #FDDCCC' : '1px solid transparent',
                        borderRadius: '10px',
                        color: selectedTab === tab.id ? '#E8650A' : '#374151',
                        fontSize: '13px',
                        fontWeight: selectedTab === tab.id ? 600 : 500,
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                      }}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
                
                {/* Filtre par métier - Design Premium Dropdown */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '11px',
                    fontWeight: 600,
                    color: '#6B7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    marginBottom: '8px',
                    paddingLeft: '4px',
                  }}>
                    Filtrer par métier
                  </label>
                  <div style={{ position: 'relative' }}>
                    <select
                      value={selectedMetier || 'tous'}
                      onChange={(e) => setSelectedMetier(e.target.value === 'tous' ? null : e.target.value)}
                      style={{
                        width: '100%',
                        padding: '12px 40px 12px 16px',
                        background: '#FFFFFF',
                        border: '2px solid #E5E7EB',
                        borderRadius: '12px',
                        color: '#1E3A5F',
                        fontSize: '14px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        appearance: 'none',
                        transition: 'all 0.2s',
                        outline: 'none',
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#E8650A';
                        e.target.style.boxShadow = '0 0 0 3px rgba(232, 101, 10, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#E5E7EB';
                        e.target.style.boxShadow = 'none';
                      }}
                    >
                      {METIERS_LIST.map((m) => (
                        <option key={m.value} value={m.value}>
                          {m.label}
                        </option>
                      ))}
                    </select>
                    {/* Icône chevron */}
                    <div style={{
                      position: 'absolute',
                      right: '14px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      pointerEvents: 'none',
                      color: '#6B7280',
                    }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </div>
                  </div>
                  
                  {/* Indicateur métier sélectionné */}
                  {selectedMetier && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginTop: '10px',
                      padding: '8px 12px',
                      background: '#F8FAFC',
                      borderRadius: '8px',
                      border: '1px solid #E2E8F0',
                    }}>
                      <div style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        background: METIERS_LIST.find(m => m.value === selectedMetier)?.color || '#1E3A5F',
                      }} />
                      <span style={{
                        fontSize: '13px',
                        fontWeight: 500,
                        color: '#374151',
                        flex: 1,
                      }}>
                        {METIERS_LIST.find(m => m.value === selectedMetier)?.label}
                      </span>
                      <button
                        onClick={() => setSelectedMetier(null)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          padding: '2px',
                          cursor: 'pointer',
                          color: '#9CA3AF',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* FEED CENTRAL */}
          <div style={{
            flex: 1,
            maxWidth: '600px',
            ...(embedded ? { overflowY: 'auto', height: '100%' } : {}),
          }}>
            {/* Stories Desktop */}
            <div style={{
              background: '#fff',
              borderRadius: '16px',
              padding: '16px',
              marginBottom: '16px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '12px',
              }}>
                <h3 style={{
                  fontSize: '14px',
                  fontWeight: 700,
                  color: '#1a2a4a',
                }}>
                  Stories
                </h3>
                <button style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#E8650A',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}>
                  Voir tout
                </button>
              </div>
              <div style={{
                display: 'flex',
                gap: '12px',
                overflowX: 'auto',
                paddingBottom: '4px',
              }}>
                {/* Bouton Publier Story */}
                <div 
                  onClick={handleCreateStory}
                  data-testid="desktop-story-publish-btn"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '6px',
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}
                >
                  <div style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    border: '2px dashed #E8650A',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#FFF3EC',
                    transition: 'all 0.2s',
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = '#FFE8D6';
                    e.currentTarget.style.transform = 'scale(1.05)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = '#FFF3EC';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  >
                    <Plus size={24} color="#E8650A" />
                  </div>
                  <span style={{ fontSize: '11px', color: '#555' }}>Publier</span>
                </div>
                
                {DEMO_STORIES.map((story) => (
                  <div key={story.id} style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '6px',
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}>
                    <div style={{
                      width: '64px',
                      height: '64px',
                      borderRadius: '50%',
                      border: `2.5px solid ${story.viewed ? '#D1D5DB' : '#E8650A'}`,
                      padding: '2px',
                    }}>
                      <img 
                        src={story.author_photo}
                        alt={story.author_name}
                        style={{
                          width: '100%',
                          height: '100%',
                          borderRadius: '50%',
                          objectFit: 'cover',
                        }}
                      />
                    </div>
                    <span style={{
                      fontSize: '11px',
                      color: '#555',
                      maxWidth: '64px',
                      textAlign: 'center',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {story.author_name.split(' ')[0]}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Zone de publication */}
            <div style={{
              background: '#fff',
              borderRadius: '16px',
              padding: '16px',
              marginBottom: '16px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '50%',
                  background: '#E8650A',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '18px',
                }}>
                  T
                </div>
                <button 
                  onClick={handleOpenQuickPost}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    background: '#F3F4F6',
                    border: 'none',
                    borderRadius: '24px',
                    textAlign: 'left',
                    color: '#9CA3AF',
                    fontSize: '14px',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                  onMouseOver={(e) => e.currentTarget.style.background = '#E5E7EB'}
                  onMouseOut={(e) => e.currentTarget.style.background = '#F3F4F6'}
                >
                  Partage ton chantier, ton conseil, ta réalisation...
                </button>
              </div>
            </div>

            {/* Posts */}
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>Chargement...</div>
            ) : posts.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>Aucune publication pour le moment</div>
            ) : (
              posts.map((post, index) => (
                <div key={post._id || post.id || index} style={{
                  background: '#fff',
                  borderRadius: '16px',
                  marginBottom: '16px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                  overflow: 'hidden',
                }}>
                  <PostCard post={post} onNavigateToPro={handleNavigateToPro} />
                </div>
              ))
            )}
          </div>

          {/* SIDEBAR DROITE */}
          <div style={{
            width: '320px',
            flexShrink: 0,
            ...(embedded ? { overflowY: 'auto', height: '100%' } : {}),
          }}>
            <div style={embedded ? {} : {
              position: 'sticky',
              top: '100px',
              maxHeight: 'calc(100vh - 120px)',
              overflowY: 'auto',
            }}>
              {/* Événements à venir - État vide production */}
              <div style={{
                background: '#fff',
                borderRadius: '16px',
                padding: '16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              }}>
                <h3 style={{ fontSize: '14px', fontWeight: 700, color: '#1a2a4a', marginBottom: '16px' }}>
                  🎪 Événements à venir
                </h3>
                <div style={{
                  textAlign: 'center',
                  padding: '20px 0',
                  color: '#9CA3AF',
                  fontSize: '13px',
                }}>
                  Aucun événement pour le moment
                </div>
                <button 
                  onClick={() => navigate('/reseau/evenements')}
                  style={{
                    width: '100%',
                    padding: '10px',
                    marginTop: '8px',
                    background: '#FFF3EC',
                    border: '1px solid #E8650A',
                    borderRadius: '8px',
                    color: '#E8650A',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Voir tous les événements
                </button>
              </div>
              
              {/* Pros actifs - État vide production */}
              <div style={{
                background: '#fff',
                borderRadius: '16px',
                padding: '16px',
                marginTop: '16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              }}>
                <h3 style={{ fontSize: '14px', fontWeight: 700, color: '#1a2a4a', marginBottom: '16px' }}>
                  👷 Pros actifs
                </h3>
                <div style={{
                  textAlign: 'center',
                  padding: '20px 0',
                  color: '#9CA3AF',
                  fontSize: '13px',
                }}>
                  Aucun pro actif pour le moment
                </div>
              </div>
            </div>
          </div>
      </div>

      {/* ═══ FEED MOBILE / TABLETTE ═══ */}
      <div className="lg:hidden" style={{ padding: '8px 0', paddingBottom: '80px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
            Chargement...
          </div>
        ) : posts.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
            Aucune publication pour le moment
          </div>
        ) : (
          posts.map((post, index) => (
            <PostCard 
              key={post._id || post.id || index} 
              post={post} 
              onNavigateToPro={handleNavigateToPro}
            />
          ))
        )}
      </div>

      {/* PAGE RECHERCHE RÉSEAU */}
      <SearchPage isOpen={searchOpen} onClose={() => setSearchOpen(false)} />

      {/* ═══ SYSTÈME DE FILTRES UNIFIÉ - Réseau ═══ */}
      <UnifiedFilterSystem
        context="reseau"
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        filters={filters}
        onFiltersChange={setFilters}
        resultsCount={posts.length}
        onApply={() => setShowFilters(false)}
      />
      
      {/* ═══ POPUP INSCRIPTION POUR STORIES ═══ */}
      <AnimatePresence>
        {showAuthPopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 100,
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '16px',
            }}
            onClick={() => setShowAuthPopup(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              style={{
                background: '#FFFFFF',
                borderRadius: '20px',
                padding: '32px',
                maxWidth: '420px',
                width: '100%',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                position: 'relative',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Icône */}
              <div style={{
                width: '72px',
                height: '72px',
                margin: '0 auto 20px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #FFF3EC 0%, #FFE4D6 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <ImageIcon size={36} color="#E8650A" />
              </div>
              
              {/* Titre */}
              <h3 style={{
                fontSize: '22px',
                fontWeight: 700,
                textAlign: 'center',
                color: '#1a2a4a',
                marginBottom: '8px',
              }}>
                Partage ta story !
              </h3>
              
              {/* Description */}
              <p style={{
                fontSize: '14px',
                color: '#6B7280',
                textAlign: 'center',
                marginBottom: '24px',
                lineHeight: 1.5,
              }}>
                Inscris-toi gratuitement pour publier des stories, suivre des pros et interagir avec la communauté.
              </p>
              
              {/* Avantages */}
              <div style={{ marginBottom: '24px' }}>
                {[
                  'Photos et vidéos jusqu\'à 90 secondes',
                  'Ajoute du texte et des emojis',
                  'Visible pendant 7 jours',
                ].map((text, idx) => (
                  <div key={idx} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '10px',
                  }}>
                    <div style={{
                      width: '22px',
                      height: '22px',
                      borderRadius: '50%',
                      background: '#DCFCE7',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <span style={{ color: '#16A34A', fontSize: '12px', fontWeight: 700 }}>✓</span>
                    </div>
                    <span style={{ fontSize: '14px', color: '#374151' }}>{text}</span>
                  </div>
                ))}
              </div>
              
              {/* Boutons */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <button
                  onClick={() => {
                    setShowAuthPopup(false);
                    navigate('/inscription');
                  }}
                  style={{
                    width: '100%',
                    padding: '14px 24px',
                    background: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)',
                    color: '#FFFFFF',
                    fontWeight: 600,
                    fontSize: '15px',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                  onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                  Créer mon compte gratuit
                </button>
                <button
                  onClick={() => {
                    setShowAuthPopup(false);
                    navigate('/connexion');
                  }}
                  style={{
                    width: '100%',
                    padding: '14px 24px',
                    background: '#F3F4F6',
                    color: '#374151',
                    fontWeight: 500,
                    fontSize: '15px',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onMouseOver={(e) => e.currentTarget.style.background = '#E5E7EB'}
                  onMouseOut={(e) => e.currentTarget.style.background = '#F3F4F6'}
                >
                  J'ai déjà un compte
                </button>
              </div>
              
              {/* Bouton Fermer */}
              <button
                onClick={() => setShowAuthPopup(false)}
                style={{
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                  background: 'transparent',
                  border: 'none',
                  padding: '8px',
                  cursor: 'pointer',
                  color: '#9CA3AF',
                  borderRadius: '8px',
                  transition: 'all 0.2s',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = '#F3F4F6';
                  e.currentTarget.style.color = '#374151';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#9CA3AF';
                }}
              >
                <X size={22} />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* ═══ MODAL PUBLICATION RAPIDE (Style Facebook) ═══ */}
      <AnimatePresence>
        {showQuickPost && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 100,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px',
            }}
            onClick={handleCloseQuickPost}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              style={{
                background: '#FFFFFF',
                borderRadius: '16px',
                width: '100%',
                maxWidth: '520px',
                maxHeight: '90vh',
                overflow: 'hidden',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px 20px',
                borderBottom: '1px solid #E5E7EB',
              }}>
                <div style={{ width: '36px' }} />
                <h2 style={{
                  fontSize: '18px',
                  fontWeight: 700,
                  color: '#1a2a4a',
                }}>
                  Créer une publication
                </h2>
                <button
                  onClick={handleCloseQuickPost}
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    background: '#F3F4F6',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#6B7280',
                    transition: 'all 0.15s',
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = '#E5E7EB';
                    e.currentTarget.style.color = '#374151';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = '#F3F4F6';
                    e.currentTarget.style.color = '#6B7280';
                  }}
                >
                  <X size={20} />
                </button>
              </div>
              
              {/* User info */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '16px 20px 8px',
              }}>
                <div style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #1E3A5F 0%, #2D4A6F 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#FFFFFF',
                  fontWeight: 700,
                  fontSize: '16px',
                }}>
                  {localStorage.getItem('user_name')?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <div>
                  <p style={{ fontWeight: 600, color: '#1a2a4a', fontSize: '15px' }}>
                    {localStorage.getItem('user_name') || 'Mon profil'}
                  </p>
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '3px 8px',
                    background: '#F3F4F6',
                    borderRadius: '6px',
                    fontSize: '12px',
                    color: '#6B7280',
                    marginTop: '2px',
                  }}>
                    🌍 Public
                  </div>
                </div>
              </div>
              
              {/* Textarea */}
              <div style={{ padding: '8px 20px 16px' }}>
                <textarea
                  value={postContent}
                  onChange={(e) => setPostContent(e.target.value)}
                  placeholder="Qu'as-tu à partager aujourd'hui ?"
                  style={{
                    width: '100%',
                    minHeight: '120px',
                    border: 'none',
                    outline: 'none',
                    resize: 'none',
                    fontSize: '17px',
                    color: '#1a2a4a',
                    lineHeight: 1.5,
                    fontFamily: 'inherit',
                  }}
                  autoFocus
                />
                
                {/* Preview Photo unique (format 4:5) */}
                {postPhoto && !isGalleryMode && (
                  <div style={{ marginTop: '12px' }}>
                    <div style={{ 
                      position: 'relative', 
                      borderRadius: '12px', 
                      overflow: 'hidden',
                      aspectRatio: '4/5',
                      maxWidth: '300px',
                    }}>
                      <img 
                        src={postPhoto} 
                        alt="" 
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                      <button
                        onClick={() => setPostPhoto(null)}
                        style={{
                          position: 'absolute',
                          top: '8px',
                          right: '8px',
                          width: '28px',
                          height: '28px',
                          borderRadius: '50%',
                          background: 'rgba(0,0,0,0.6)',
                          border: 'none',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#FFFFFF',
                        }}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Preview Galerie (format 4:5) */}
                {isGalleryMode && galleryPhotos.length > 0 && (
                  <div style={{ marginTop: '12px' }}>
                    {/* Titre de la galerie */}
                    <input
                      type="text"
                      value={galleryTitle}
                      onChange={(e) => setGalleryTitle(e.target.value)}
                      placeholder="Titre de la galerie *"
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        marginBottom: '12px',
                        border: '1px solid #E5E7EB',
                        borderRadius: '10px',
                        fontSize: '14px',
                        fontWeight: 500,
                        outline: 'none',
                      }}
                    />
                    
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '8px',
                    }}>
                      <span style={{ fontSize: '12px', fontWeight: 500, color: '#6B7280' }}>
                        {galleryPhotos.length}/6 photos
                      </span>
                      {galleryPhotos.length < 6 && (
                        <label style={{
                          fontSize: '12px',
                          color: '#E8650A',
                          fontWeight: 500,
                          cursor: 'pointer',
                        }}>
                          + Ajouter
                          <input 
                            type="file" 
                            accept="image/*" 
                            multiple 
                            style={{ display: 'none' }}
                            onChange={(e) => {
                              const files = Array.from(e.target.files || []);
                              const remaining = 6 - galleryPhotos.length;
                              if (files.length > remaining) {
                                toast.error(`Tu peux ajouter encore ${remaining} photo(s) maximum`);
                              }
                              files.slice(0, remaining).forEach(file => {
                                const reader = new FileReader();
                                reader.onload = (ev) => {
                                  setGalleryPhotos(prev => [...prev, ev.target.result].slice(0, 6));
                                };
                                reader.readAsDataURL(file);
                              });
                            }}
                          />
                        </label>
                      )}
                    </div>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: galleryPhotos.length === 1 ? '1fr' : galleryPhotos.length === 2 ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
                      gap: '4px',
                      borderRadius: '12px',
                      overflow: 'hidden',
                    }}>
                      {galleryPhotos.map((photo, idx) => (
                        <div key={idx} style={{ position: 'relative', aspectRatio: '4/5' }}>
                          <img 
                            src={photo} 
                            alt="" 
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                          <button
                            onClick={() => setGalleryPhotos(galleryPhotos.filter((_, i) => i !== idx))}
                            style={{
                              position: 'absolute',
                              top: '8px',
                              right: '8px',
                              width: '28px',
                              height: '28px',
                              borderRadius: '50%',
                              background: 'rgba(0,0,0,0.6)',
                              border: 'none',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#FFFFFF',
                            }}
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Location & Feeling badges */}
                <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
                  {isGalleryMode && (
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 12px',
                      background: '#F0FDF4',
                      borderRadius: '20px',
                      fontSize: '13px',
                      color: '#16A34A',
                    }}>
                      <ImageIcon size={14} />
                      Galerie
                      <button 
                        onClick={() => { setIsGalleryMode(false); setGalleryPhotos([]); setGalleryTitle(''); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#16A34A' }}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  )}
                  {postLocation && (
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 12px',
                      background: '#EEF2FF',
                      borderRadius: '20px',
                      fontSize: '13px',
                      color: '#4F46E5',
                    }}>
                      <MapPin size={14} />
                      {postLocation}
                      <button 
                        onClick={() => setPostLocation('')}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#4F46E5' }}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  )}
                  {postFeeling && (
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 12px',
                      background: '#FEF3C7',
                      borderRadius: '20px',
                      fontSize: '13px',
                      color: '#D97706',
                    }}>
                      {postFeeling}
                      <button 
                        onClick={() => setPostFeeling('')}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#D97706' }}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  )}
                  {postProductLink && (
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 12px',
                      background: '#FFF3EC',
                      borderRadius: '20px',
                      fontSize: '13px',
                      color: '#E8650A',
                    }}>
                      <Link2 size={14} />
                      Produit lié
                      <button 
                        onClick={() => setPostProductLink(null)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#E8650A' }}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Action buttons bar */}
              <div style={{
                margin: '0 20px',
                padding: '12px 16px',
                border: '1px solid #E5E7EB',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
                <span style={{ fontSize: '14px', fontWeight: 600, color: '#1a2a4a' }}>
                  Ajouter à ta publication
                </span>
                <div style={{ display: 'flex', gap: '4px' }}>
                  {/* Photo (1 seule, format 4:5) */}
                  <label style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: isGalleryMode ? 'not-allowed' : 'pointer',
                    transition: 'all 0.15s',
                    color: isGalleryMode ? '#9CA3AF' : '#22C55E',
                    opacity: isGalleryMode ? 0.5 : 1,
                  }}
                  onMouseOver={(e) => { if (!isGalleryMode) e.currentTarget.style.background = '#F0FDF4'; }}
                  onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                  title="Photo (format 4:5)"
                  >
                    <Camera size={22} />
                    <input 
                      type="file" 
                      accept="image/*"
                      style={{ display: 'none' }}
                      disabled={isGalleryMode}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onload = (ev) => {
                            setPostPhoto(ev.target.result);
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                    />
                  </label>
                  
                  {/* Galerie (plusieurs photos, format 4:5) */}
                  <label style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    color: isGalleryMode ? '#16A34A' : '#8B5CF6',
                    background: isGalleryMode ? '#F0FDF4' : 'transparent',
                    border: isGalleryMode ? '2px solid #22C55E' : 'none',
                  }}
                  onMouseOver={(e) => { if (!isGalleryMode) e.currentTarget.style.background = '#F5F3FF'; }}
                  onMouseOut={(e) => { if (!isGalleryMode) e.currentTarget.style.background = 'transparent'; }}
                  title="Galerie (max 6 photos)"
                  >
                    <ImageIcon size={22} />
                    <input 
                      type="file" 
                      accept="image/*" 
                      multiple 
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        if (files.length > 6) {
                          toast.error('Maximum 6 photos pour une galerie');
                        }
                        setIsGalleryMode(true);
                        setPostPhoto(null);
                        files.slice(0, 6).forEach(file => {
                          const reader = new FileReader();
                          reader.onload = (ev) => {
                            setGalleryPhotos(prev => [...prev, ev.target.result].slice(0, 6));
                          };
                          reader.readAsDataURL(file);
                        });
                      }}
                    />
                  </label>
                  
                  {/* Video */}
                  <label style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    color: '#EF4444',
                  }}
                  onMouseOver={(e) => e.currentTarget.style.background = '#FEF2F2'}
                  onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <Video size={22} />
                    <input 
                      type="file" 
                      accept="video/*" 
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          // Max 3 minutes = 180 seconds
                          const video = document.createElement('video');
                          video.preload = 'metadata';
                          video.onloadedmetadata = () => {
                            if (video.duration > 180) {
                              toast.error('La vidéo doit faire moins de 3 minutes');
                            } else {
                              setPostVideo(file);
                              toast.success('Vidéo ajoutée');
                            }
                          };
                          video.src = URL.createObjectURL(file);
                        }
                      }}
                    />
                  </label>
                  
                  {/* Location */}
                  <button 
                    onClick={() => {
                      const loc = prompt('Où es-tu ?');
                      if (loc) setPostLocation(loc);
                    }}
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      color: '#EF4444',
                      background: 'transparent',
                      border: 'none',
                    }}
                    onMouseOver={(e) => e.currentTarget.style.background = '#FEF2F2'}
                    onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <MapPin size={22} />
                  </button>
                  
                  {/* Feeling */}
                  <button 
                    onClick={() => {
                      const feelings = ['😊 content', '💪 fier', '🔥 motivé', '🎉 excité', '😴 fatigué', '🤔 pensif'];
                      const choice = prompt(`Comment te sens-tu ?\n${feelings.join('\n')}`);
                      if (choice) setPostFeeling(choice);
                    }}
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      color: '#F59E0B',
                      background: 'transparent',
                      border: 'none',
                    }}
                    onMouseOver={(e) => e.currentTarget.style.background = '#FFFBEB'}
                    onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <Smile size={22} />
                  </button>
                  
                  {/* Link product */}
                  <button 
                    onClick={() => {
                      setPostProductLink({ name: 'Produit exemple' });
                      toast.success('Lien produit ajouté');
                    }}
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      color: '#E8650A',
                      background: 'transparent',
                      border: 'none',
                    }}
                    onMouseOver={(e) => e.currentTarget.style.background = '#FFF3EC'}
                    onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                    title="Lier un produit ou service"
                  >
                    <Link2 size={22} />
                  </button>
                </div>
              </div>
              
              {/* Submit button */}
              <div style={{ padding: '16px 20px' }}>
                <button
                  onClick={handlePublishPost}
                  disabled={isSubmitting || (!postContent.trim() && !postPhoto && galleryPhotos.length === 0 && !postVideo)}
                  style={{
                    width: '100%',
                    padding: '14px',
                    background: (postContent.trim() || postPhoto || galleryPhotos.length > 0 || postVideo) 
                      ? 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)' 
                      : '#E5E7EB',
                    color: (postContent.trim() || postPhoto || galleryPhotos.length > 0 || postVideo) ? '#FFFFFF' : '#9CA3AF',
                    fontWeight: 600,
                    fontSize: '15px',
                    border: 'none',
                    borderRadius: '10px',
                    cursor: (postContent.trim() || postPhoto || galleryPhotos.length > 0 || postVideo) ? 'pointer' : 'not-allowed',
                    transition: 'all 0.2s',
                  }}
                >
                  {isSubmitting ? 'Publication en cours...' : 'Publier'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
